import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { uhcPlanSchema } from "~/schemas/SignUp/InsuranceProvider";
import { RadioCardGroup } from "~/shared/components/ui/RadioCardGroup";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { InsuranceProviderFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { getProviderPlanOptions } from "~/utils";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

export const UhcPlanForm = ({
  onPrev,
  onNext,
  stepId = "primary_insurance",
  isSecondaryProvider = false,
}: InsuranceProviderFormProps & { stepId?: string }) => {
  const { patient, setPatientInsuranceField } = useSignUpStore();
  const { primaryInsurance, secondaryInsurance } = patient;

  const planTypeProvider = isSecondaryProvider
    ? secondaryInsurance?.planTypeProvider
    : primaryInsurance.planTypeProvider;

  const defaultValue = isSecondaryProvider
    ? secondaryInsurance?.uhcPlan
    : primaryInsurance.uhcPlan;

  const [selectedValue, setSelectedValue] = useState<string>(
    defaultValue ?? "",
  );
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(Boolean(defaultValue));

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: isSecondaryProvider
      ? "secondary_insurance"
      : "primary_insurance",
    subStep: "uhc_plan",
  });

  // Get plan options from config
  const planOptions = getProviderPlanOptions("uhc", planTypeProvider) || [];

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof uhcPlanSchema>>({
    mode: "onBlur",
    resolver: zodResolver(uhcPlanSchema),
    defaultValues: {
      uhcPlan: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setSelectedValue(defaultValue ?? "");
    setValue("uhcPlan", defaultValue ?? "");
  }, [defaultValue, setValue]);

  const handleSelectionChange = (value: string) => {
    setSelectedValue(value);
    setValue("uhcPlan", value, { shouldValidate: true });
    // On the frontier (first encounter), a selection advances immediately;
    // on a revisited step the user changes freely and clicks Next.
    if (autoAdvanceOnSelect) {
      void handleSubmit(onSubmit, onError)();
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof uhcPlanSchema>> = (data) => {
    setPatientInsuranceField("uhcPlan", data.uhcPlan, isSecondaryProvider);
    trackComplete({ uhc_plan: data.uhcPlan });
    onNext();
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientInsuranceField("uhcPlan", undefined, isSecondaryProvider);
    trackBack();
    onPrev();
  };

  const isMedicare = planTypeProvider === "medicare";
  const canProceed = selectedValue !== "";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="w-full max-w-[570px] rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <h1 className="font-display text-xl sm:text-2xl">
          {isMedicare
            ? "Is that UHC Medicare Advantage or UHC Dual Complete?"
            : "Is that UHC Community or UHC Dual Complete?"}
        </h1>
        <RadioCardGroup
          options={planOptions}
          value={selectedValue}
          onChange={handleSelectionChange}
        />
        {errors.uhcPlan && (
          <ErrorMessage message={errors.uhcPlan.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons
        onPrev={handlePrev}
        isNextDisabled={!canProceed}
        helperText="Select a plan to continue"
      />
    </form>
  );
};
